/**
 * Özet line charts (v0.1.266; Sercan, 3 Nov 2026: "bunları aynı şekilde özet sayfamıza koyalım"): this period against
 * the previous one by day index (sales, cumulative or daily; visits by the chosen measure), and day by day over both
 * periods with the start of this one marked (visits by source, mails, YouTube views, Pinterest impressions). The chart
 * rules of ozet-charts.tsx hold: the takeaway on top, the highest and the last value of the main line written on the
 * chart, the picked day said in the readout row (a tap, a click or an arrow key picks), dates as "2 Kas", one colour
 * per series, never the accent, every number in a screen reader table. Lines are drawn by hand in a stretched SVG
 * (non-scaling strokes); the dots and value labels are HTML over it so they keep their shape. A null day is a gap.
 */

import { SITE_SOURCE_GROUP_LABELS, SITE_SOURCE_GROUPS, type SiteSourceGroup } from '@twin/contracts';
import { type ReactNode, useState } from 'react';
import type { OzetRange, OzetSummaryDay } from '../api/ozet.js';
import { formatIsoDay, formatNumber } from '../lib/format.js';
import { ChartFrame, type ChartPick, ChartReadout, ChartTakeaway, pickHandlers, useChartPick } from './chart-kit.js';
import { CountedValueLabels, centsText } from './ozet-charts.js';
import {
  type AlignedPoint,
  alignByDayIndex,
  biggest,
  comparedSums,
  cumulative,
  isolatedPoints,
  linePath,
  maxOf,
  pointX,
} from './ozet-series.js';
import { SOURCE_COLOURS } from './site-sources-chart.js';

const WIDTH = 600;
const HEIGHT = 160;

function n(value: number): string {
  return formatNumber(value);
}

/** A dot's height in percent of the plot, on the line drawn by pointY. */
function dotBottom(value: number, max: number): number {
  return ((1 + (Math.max(0, value) / Math.max(1, max)) * (HEIGHT - 4)) / HEIGHT) * 100;
}

function dayText(day: string | null): string {
  return day === null ? '-' : formatIsoDay(day);
}

interface LineSeries {
  key: string;
  label: string;
  colour: string;
  values: readonly (number | null)[];
  /** The previous period: drawn thin and dashed. */
  muted?: boolean;
}

interface SrTable {
  caption: string;
  columns: readonly string[];
  rows: readonly { head: string; cells: readonly string[] }[];
}

/** One figure: takeaway, optional controls, the plot (a slider over the days), readout, ends, legend, sr table. */
function LineFigure({
  id,
  frameLabel,
  title,
  takeaway,
  controls,
  count,
  series,
  pick,
  readout,
  labelled,
  labelText = n,
  markerIndex,
  ends,
  table,
  note,
}: {
  id: string;
  frameLabel: string;
  title: string;
  takeaway: string;
  controls?: ReactNode;
  /** The number of days on the x axis; never taken from the drawn lines, which can all be switched off. */
  count: number;
  series: readonly LineSeries[];
  pick: ChartPick;
  readout: string;
  /** The series whose highest and last value are written on the chart. */
  labelled?: readonly (number | null)[] | undefined;
  labelText?: (value: number) => string;
  /** The first day of the current period, marked with a thin rule. */
  markerIndex?: number;
  ends: readonly [string, string];
  table: SrTable;
  note?: ReactNode;
}) {
  const max = maxOf(...series.map((line) => line.values));
  const titleId = `${id}-title`;
  return (
    <figure className="sx-src oz-chart oz-lines">
      <ChartTakeaway>{takeaway}</ChartTakeaway>
      {controls}
      <ChartFrame label={frameLabel} count={count} pick={pick} valueText={readout}>
        <div className="sx-src__plot">
          <svg
            className="sx-src__svg"
            viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
            preserveAspectRatio="none"
            role="img"
            aria-labelledby={titleId}
          >
            <title id={titleId}>{title}</title>
            {[0.5, 1].map((share) => (
              <line
                key={share}
                className="sx-src__grid"
                x1={0}
                x2={WIDTH}
                y1={HEIGHT - 1 - share * (HEIGHT - 4)}
                y2={HEIGHT - 1 - share * (HEIGHT - 4)}
              />
            ))}
            <line className="au-bars__base" x1={0} y1={HEIGHT - 0.5} x2={WIDTH} y2={HEIGHT - 0.5} />
            {markerIndex === undefined || markerIndex <= 0 || markerIndex >= count ? null : (
              <line
                className="oz-lines__mark"
                x1={(markerIndex / count) * WIDTH}
                x2={(markerIndex / count) * WIDTH}
                y1={0}
                y2={HEIGHT}
              />
            )}
            {pick.index < 0 ? null : (
              <line
                className="oz-lines__focus"
                x1={pointX(pick.index, count, WIDTH)}
                x2={pointX(pick.index, count, WIDTH)}
                y1={0}
                y2={HEIGHT}
              />
            )}
            {Array.from({ length: count }, (_, index) => (
              <rect
                // biome-ignore lint/suspicious/noArrayIndexKey: one hit area per day index, never reordered.
                key={index}
                className="sx-src__hit"
                x={(index / count) * WIDTH}
                y={0}
                width={WIDTH / count}
                height={HEIGHT}
                {...pickHandlers(pick, index)}
              />
            ))}
            {series.map((line) => {
              const d = linePath(line.values, max, WIDTH, HEIGHT);
              return d === '' ? null : (
                <path
                  key={line.key}
                  className={line.muted ? 'oz-lines__path oz-lines__path--prev' : 'oz-lines__path'}
                  d={d}
                  stroke={line.colour}
                  pointerEvents="none"
                />
              );
            })}
          </svg>
          {series.flatMap((line) => {
            const dots = new Set(isolatedPoints(line.values));
            if (pick.index >= 0 && line.values[pick.index] !== null && line.values[pick.index] !== undefined) {
              dots.add(pick.index);
            }
            return [...dots].map((index) => {
              const value = line.values[index] ?? 0;
              return (
                <span
                  key={`${line.key}-${index}`}
                  className={line.muted ? 'oz-dot oz-dot--prev' : 'oz-dot'}
                  style={{
                    left: `${((index + 0.5) / Math.max(1, count)) * 100}%`,
                    bottom: `${dotBottom(value, max)}%`,
                    background: line.colour,
                  }}
                  aria-hidden="true"
                />
              );
            });
          })}
          {markerIndex === undefined || markerIndex <= 0 || markerIndex >= count ? null : (
            <span
              className={
                markerIndex / count > 0.7 ? 'oz-lines__mark-label oz-lines__mark-label--end' : 'oz-lines__mark-label'
              }
              style={{ left: `${(markerIndex / count) * 100}%` }}
              aria-hidden="true"
            >
              bu dönem
            </span>
          )}
          {labelled === undefined ? null : (
            <CountedValueLabels
              values={labelled.map((value) => value ?? 0)}
              counted={labelled.map((value) => value !== null)}
              max={max}
              text={labelText}
            />
          )}
        </div>
      </ChartFrame>
      <ChartReadout>{readout}</ChartReadout>
      <figcaption className="au-bars__cap" aria-hidden="true">
        <span>{ends[0]}</span>
        <span>{ends[1]}</span>
      </figcaption>
      <ul className="sx-src__legend" aria-label="Çizgiler">
        {series.map((line) => (
          <li key={line.key}>
            <span
              className={line.muted ? 'oz-swatch-line oz-swatch-line--prev' : 'oz-swatch-line'}
              style={{ borderColor: line.colour }}
              aria-hidden="true"
            />
            <span>{line.label}</span>
          </li>
        ))}
      </ul>
      {note === undefined ? null : <p className="oz-note">{note}</p>}
      <div className="sr-only">
        <table>
          <caption>{table.caption}</caption>
          <thead>
            <tr>
              {table.columns.map((column) => (
                <th key={column} scope="col">
                  {column}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {table.rows.map((row) => (
              <tr key={row.head}>
                <th scope="row">{row.head}</th>
                {row.cells.map((cell, index) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: cells follow the fixed column order.
                  <td key={index}>{cell}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </figure>
  );
}

/** A row of pressed/unpressed buttons: one choice (radio-like) or several (each toggles). */
function Toggles<K extends string>({
  label,
  options,
  isOn,
  onPress,
}: {
  label: string;
  options: readonly { key: K; label: string; colour?: string }[];
  isOn: (key: K) => boolean;
  onPress: (key: K) => void;
}) {
  return (
    <fieldset className="oz-toggle" aria-label={label}>
      {options.map((option) => (
        <button
          key={option.key}
          type="button"
          className="oz-toggle__btn"
          aria-pressed={isOn(option.key)}
          onClick={() => onPress(option.key)}
        >
          {option.colour === undefined ? null : (
            <span className="oz-toggle__swatch" style={{ background: option.colour }} aria-hidden="true" />
          )}
          {option.label}
        </button>
      ))}
    </fieldset>
  );
}

const CURRENT_COLOUR = 'var(--data-strong)';
const PREVIOUS_COLOUR = 'var(--ink-3)';

function valueOr(value: number | null, text: (v: number) => string, missing: string): string {
  return value === null ? missing : text(value);
}

/** Bu dönem ve önceki dönem: Etsy revenue by day index, cumulative or per day. */
export function OzetPeriodSalesChart({
  daily,
  current,
  previous,
  previousName,
  previousIn,
}: {
  daily: readonly OzetSummaryDay[];
  current: OzetRange;
  previous: OzetRange;
  previousName: string;
  /** The previous period with "in": "geçen haftanın aynı günlerinde". */
  previousIn: string;
}) {
  const [mode, setMode] = useState<'cumulative' | 'daily'>('cumulative');
  const points = alignByDayIndex(daily, current, previous, (day) => day.revenueCents);
  const pick = useChartPick(points.length);
  const now = points.map((point) => point.current);
  const before = points.map((point) => point.previous);
  const shownNow = mode === 'cumulative' ? cumulative(now) : now;
  const shownBefore = mode === 'cumulative' ? cumulative(before) : before;
  const sums = comparedSums(points);
  const picked = points[pick.index];
  const readout =
    picked === undefined
      ? ''
      : `${pick.index + 1}. gün · bu dönem ${dayText(picked.currentDay)}: ${valueOr(shownNow[pick.index] ?? null, centsText, 'henüz yok')} · önceki ${dayText(picked.previousDay)}: ${valueOr(shownBefore[pick.index] ?? null, centsText, 'yok')}${mode === 'cumulative' ? ' (birikimli)' : ''}`;
  return (
    <LineFigure
      id="oz-period-sales"
      frameLabel="Bu dönem ve önceki dönem: gün seç"
      title={`Etsy geliri, bu dönem ve ${previousName}, gün sırasına göre${mode === 'cumulative' ? ', birikimli' : ''}.`}
      takeaway={`Bu dönemde ${centsText(sums.current ?? 0)}; ${previousIn} ${centsText(sums.previous ?? 0)}`}
      controls={
        <Toggles
          label="Gösterim"
          options={[
            { key: 'cumulative', label: 'Birikimli' },
            { key: 'daily', label: 'Günlük' },
          ]}
          isOn={(key) => key === mode}
          onPress={setMode}
        />
      }
      count={points.length}
      series={[
        {
          key: 'prev',
          label: `Önceki dönem (${previousName})`,
          colour: PREVIOUS_COLOUR,
          values: shownBefore,
          muted: true,
        },
        { key: 'now', label: 'Bu dönem', colour: CURRENT_COLOUR, values: shownNow },
      ]}
      pick={pick}
      readout={readout}
      labelled={shownNow}
      labelText={centsText}
      ends={['1. gün', `${points.length}. gün`]}
      table={{
        caption: 'Etsy geliri, bu dönem ve önceki dönem, gün sırasına göre',
        columns: ['Gün', 'Bu dönem', 'Önceki dönem'],
        rows: points.map((point, index) => ({
          head: `${index + 1}. gün (${dayText(point.currentDay)} / ${dayText(point.previousDay)})`,
          cells: [
            valueOr(shownNow[index] ?? null, centsText, '-'),
            valueOr(shownBefore[index] ?? null, centsText, '-'),
          ],
        })),
      }}
    />
  );
}

type VisitMeasure = 'sessions' | 'pageViews' | 'entries';

const VISIT_MEASURES: readonly { key: VisitMeasure; label: string; unit: string; missing: string }[] = [
  { key: 'sessions', label: 'Oturum', unit: 'oturum', missing: 'ertesi sabah gelir' },
  { key: 'pageViews', label: 'Sayfa görüntüleme', unit: 'sayfa', missing: 'kayıt yok' },
  { key: 'entries', label: 'Dışarıdan giriş', unit: 'giriş', missing: 'kayıt yok' },
];

/** Günlük ziyaret: one measure by day index, this period solid, the previous one muted; null days are gaps. */
export function OzetDailyVisitsChart({
  daily,
  current,
  previous,
  previousName,
  previousIn,
}: {
  daily: readonly OzetSummaryDay[];
  current: OzetRange;
  previous: OzetRange;
  previousName: string;
  /** The previous period with "in": "geçen haftanın aynı günlerinde". */
  previousIn: string;
}) {
  const [measure, setMeasure] = useState<VisitMeasure>('sessions');
  const spec = VISIT_MEASURES.find((item) => item.key === measure) ?? VISIT_MEASURES[0];
  const points = alignByDayIndex(daily, current, previous, (day) => day[measure]);
  const pick = useChartPick(points.length);
  const now = points.map((point) => point.current);
  const before = points.map((point) => point.previous);
  const sums = comparedSums(points);
  const picked = points[pick.index];
  const unit = spec?.unit ?? '';
  const missing = spec?.missing ?? 'yok';
  const readout =
    picked === undefined
      ? ''
      : `${pick.index + 1}. gün · bu dönem ${dayText(picked.currentDay)}: ${valueOr(picked.current, (v) => `${n(v)} ${unit}`, picked.currentDay === null ? 'yok' : missing)} · önceki ${dayText(picked.previousDay)}: ${valueOr(picked.previous, (v) => `${n(v)} ${unit}`, 'yok')}`;
  const takeaway =
    sums.current === null
      ? `Bu dönemin ${unit} sayısı henüz gelmedi`
      : `Bu dönemde ${n(sums.current)} ${unit}; ${previousIn} ${sums.previous === null ? 'veri yok' : n(sums.previous)}${sums.days < current.days ? ` (ilk ${n(sums.days)} gün)` : ''}`;
  return (
    <LineFigure
      id="oz-daily-visits"
      frameLabel="Günlük ziyaret: gün seç"
      title={`Günlük ${spec?.label.toLocaleLowerCase('tr-TR') ?? ''}, bu dönem ve ${previousName}, gün sırasına göre.`}
      takeaway={takeaway}
      controls={
        <Toggles
          label="Ölçü"
          options={VISIT_MEASURES.map((item) => ({ key: item.key, label: item.label }))}
          isOn={(key) => key === measure}
          onPress={setMeasure}
        />
      }
      count={points.length}
      series={[
        { key: 'prev', label: `Önceki dönem (${previousName})`, colour: PREVIOUS_COLOUR, values: before, muted: true },
        { key: 'now', label: 'Bu dönem', colour: CURRENT_COLOUR, values: now },
      ]}
      pick={pick}
      readout={readout}
      labelled={now}
      ends={['1. gün', `${points.length}. gün`]}
      table={{
        caption: `Günlük ${spec?.label ?? ''}, bu dönem ve önceki dönem`,
        columns: ['Gün', 'Bu dönem', 'Önceki dönem'],
        rows: points.map((point, index) => ({
          head: `${index + 1}. gün (${dayText(point.currentDay)} / ${dayText(point.previousDay)})`,
          cells: [valueOr(point.current, n, '-'), valueOr(point.previous, n, '-')],
        })),
      }}
    />
  );
}

/** A colour per source group; the two without one of their own (Bing, other sites) take the neutral greys. */
const GROUP_COLOURS: Readonly<Record<SiteSourceGroup, string>> = Object.freeze({
  google: SOURCE_COLOURS.google ?? 'var(--series-1)',
  chatgpt: SOURCE_COLOURS.chatgpt ?? 'var(--series-2)',
  etsy: SOURCE_COLOURS.etsy ?? 'var(--series-3)',
  other_ai: SOURCE_COLOURS.other_ai ?? 'var(--series-4)',
  outreach: SOURCE_COLOURS.outreach ?? 'var(--series-5)',
  direct: SOURCE_COLOURS.direct ?? 'var(--data-neutral)',
  bing: 'var(--ink-3)',
  other: 'var(--line-strong)',
});

/** The index of the current period's first day in `daily` (the rule on the day-by-day charts). */
function currentStart(daily: readonly OzetSummaryDay[], current: OzetRange): number {
  const index = daily.findIndex((day) => day.day >= current.from);
  return index < 0 ? daily.length : index;
}

function inCurrent(daily: readonly OzetSummaryDay[], current: OzetRange, values: readonly (number | null)[]): number {
  let total = 0;
  daily.forEach((day, index) => {
    if (day.day >= current.from && day.day <= current.to) total += values[index] ?? 0;
  });
  return total;
}

function dayEnds(daily: readonly OzetSummaryDay[]): readonly [string, string] {
  return [formatIsoDay(daily[0]?.day ?? ''), formatIsoDay(daily.at(-1)?.day ?? '')];
}

/** Kanallara göre günlük ziyaret: a GA4 session line per source group; chips turn groups on and off. */
export function OzetSourceLinesChart({ daily, current }: { daily: readonly OzetSummaryDay[]; current: OzetRange }) {
  const valuesOf = (group: SiteSourceGroup) =>
    daily.map((day) => (day.sessions === null ? null : (day.sourceSessions[group] ?? 0)));
  const totals = new Map(
    SITE_SOURCE_GROUPS.map((group) => [group, valuesOf(group).reduce<number>((s, v) => s + (v ?? 0), 0)]),
  );
  const present = SITE_SOURCE_GROUPS.filter((group) => (totals.get(group) ?? 0) > 0);
  const [chosen, setChosen] = useState<readonly SiteSourceGroup[] | null>(null);
  const on = chosen ?? biggest(SITE_SOURCE_GROUPS, (group) => totals.get(group) ?? 0, 4);
  const pick = useChartPick(daily.length);
  if (present.length === 0) {
    return <p className="sx-empty">Google Analytics'te bu günler için henüz kaynak bilgisi yok.</p>;
  }
  const shown = present.filter((group) => on.includes(group));
  const series: LineSeries[] = shown.map((group) => ({
    key: group,
    label: SITE_SOURCE_GROUP_LABELS[group],
    colour: GROUP_COLOURS[group],
    values: valuesOf(group),
  }));
  const ranked = biggest(present, (group) => inCurrent(daily, current, valuesOf(group)), present.length);
  const lead = ranked[0];
  const picked = daily[pick.index];
  const readout =
    picked === undefined
      ? ''
      : picked.sessions === null
        ? `${formatIsoDay(picked.day)}: ziyaretçi sayımı ertesi sabah gelir`
        : `${formatIsoDay(picked.day)}: ${
            shown
              .map((group) => `${SITE_SOURCE_GROUP_LABELS[group]} ${n(picked.sourceSessions[group] ?? 0)}`)
              .join(' · ') || 'kanal seçilmedi'
          }`;
  const toggle = (group: SiteSourceGroup) =>
    setChosen(on.includes(group) ? on.filter((item) => item !== group) : [...on, group]);
  return (
    <LineFigure
      id="oz-source-lines"
      frameLabel="Kanallara göre ziyaret: gün seç"
      title={`Günlük oturum, kaynağa göre: ${dayEnds(daily).join(' ile ')} arası.`}
      takeaway={
        lead === undefined
          ? 'Bu dönemin kaynak bilgisi ertesi sabah gelir'
          : `Bu dönemde en çok ${SITE_SOURCE_GROUP_LABELS[lead]} getirdi (${n(inCurrent(daily, current, valuesOf(lead)))} oturum)`
      }
      controls={
        <Toggles
          label="Kanallar"
          options={present.map((group) => ({
            key: group,
            label: SITE_SOURCE_GROUP_LABELS[group],
            colour: GROUP_COLOURS[group],
          }))}
          isOn={(group) => on.includes(group)}
          onPress={toggle}
        />
      }
      count={daily.length}
      series={series}
      pick={pick}
      readout={readout}
      labelled={series[0]?.values}
      markerIndex={currentStart(daily, current)}
      ends={dayEnds(daily)}
      table={{
        caption: 'Günlük oturum, kaynağa göre',
        columns: ['Gün', ...present.map((group) => SITE_SOURCE_GROUP_LABELS[group])],
        rows: daily.map((day) => ({
          head: formatIsoDay(day.day),
          cells: present.map((group) => (day.sessions === null ? '-' : String(day.sourceSessions[group] ?? 0))),
        })),
      }}
    />
  );
}

type MailKey = 'messagesIn' | 'repliesOut' | 'outreachSent';

const MAIL_SERIES: readonly { key: MailKey; label: string; colour: string }[] = [
  { key: 'messagesIn', label: 'Bize gelen', colour: 'var(--series-1)' },
  { key: 'repliesOut', label: 'Müşterilere cevap', colour: 'var(--series-3)' },
  { key: 'outreachSent', label: 'Tanıtım maili', colour: 'var(--series-5)' },
];

/** Gün gün mailler: what came in, the replies to customers and the outreach mails sent; each line toggles. */
export function OzetMailLinesChart({ daily, current }: { daily: readonly OzetSummaryDay[]; current: OzetRange }) {
  const [on, setOn] = useState<readonly MailKey[]>(MAIL_SERIES.map((item) => item.key));
  const pick = useChartPick(daily.length);
  if (daily.length === 0) return <p className="sx-empty">Bu dönem için gün yok.</p>;
  const shown = MAIL_SERIES.filter((item) => on.includes(item.key));
  const series: LineSeries[] = shown.map((item) => ({
    key: item.key,
    label: item.label,
    colour: item.colour,
    values: daily.map((day) => day[item.key]),
  }));
  const sumOf = (key: MailKey) =>
    inCurrent(
      daily,
      current,
      daily.map((day) => day[key]),
    );
  const picked = daily[pick.index];
  const readout =
    picked === undefined
      ? ''
      : `${formatIsoDay(picked.day)}: ${shown.map((item) => `${item.label} ${n(picked[item.key])}`).join(' · ') || 'çizgi seçilmedi'}`;
  return (
    <LineFigure
      id="oz-mail-lines"
      frameLabel="Gün gün mailler: gün seç"
      title={`Günlük gelen mesaj, müşteri cevabı ve tanıtım maili: ${dayEnds(daily).join(' ile ')} arası.`}
      takeaway={`Bu dönemde ${n(sumOf('messagesIn'))} gelen, ${n(sumOf('repliesOut'))} cevap, ${n(sumOf('outreachSent'))} tanıtım maili`}
      controls={
        <Toggles
          label="Çizgiler"
          options={MAIL_SERIES}
          isOn={(key) => on.includes(key)}
          onPress={(key) => setOn(on.includes(key) ? on.filter((item) => item !== key) : [...on, key])}
        />
      }
      count={daily.length}
      series={series}
      pick={pick}
      readout={readout}
      labelled={series[0]?.values}
      markerIndex={currentStart(daily, current)}
      ends={dayEnds(daily)}
      note={
        <>
          Bize gelen: Twin'in okuduğu kutular. Mağaza Gmail kutusu (3dstlscan@gmail.com; Etsy ve GrabCAD bildirimleri de
          buraya gelir), site formları ve Etsy ekran görüntüleri. info@ gibi Twin'e bağlı olmayan bir kutu sayılmaz.
          Tanıtım maillerine gelen cevaplar burada değil, Tanıtım takibinde.
        </>
      }
      table={{
        caption: 'Günlük gelen mesaj, müşteri cevabı ve tanıtım maili',
        columns: ['Gün', ...MAIL_SERIES.map((item) => item.label)],
        rows: daily.map((day) => ({
          head: formatIsoDay(day.day),
          cells: MAIL_SERIES.map((item) => String(day[item.key])),
        })),
      }}
    />
  );
}

/** One channel number by day over both periods (YouTube views, Pinterest impressions); null days are gaps. */
export function OzetChannelLineChart({
  daily,
  current,
  channel,
}: {
  daily: readonly OzetSummaryDay[];
  current: OzetRange;
  channel: 'youtube' | 'pinterest';
}) {
  const pick = useChartPick(daily.length);
  const youtube = channel === 'youtube';
  const values = daily.map((day) => (youtube ? day.youtubeViews : day.pinterestImpressions));
  const unit = youtube ? 'izlenme' : 'gösterim';
  if (values.every((value) => value === null)) {
    return (
      <p className="sx-empty">
        {youtube
          ? 'YouTube için henüz iki günlük okuma yok.'
          : 'Pinterest bu günler için günlük gösterim vermedi (bağlantı ya da analiz izni yok).'}
      </p>
    );
  }
  const picked = daily[pick.index];
  const value = values[pick.index] ?? null;
  const readout =
    picked === undefined
      ? ''
      : `${formatIsoDay(picked.day)}: ${
          value === null
            ? youtube
              ? 'okuma yok'
              : 'Pinterest bir gün geriden verir'
            : `${n(value)} ${unit}${!youtube && picked.pinterestEstimated ? ' (tahmini)' : ''}`
        }`;
  const total = inCurrent(daily, current, values);
  const anyCurrent = daily.some((day, index) => day.day >= current.from && values[index] !== null);
  return (
    <LineFigure
      id={`oz-${channel}-line`}
      frameLabel={`${youtube ? 'YouTube günlük izlenme' : 'Pinterest günlük gösterim'}: gün seç`}
      title={`${youtube ? 'YouTube günlük izlenme' : 'Pinterest günlük gösterim'}: ${dayEnds(daily).join(' ile ')} arası.`}
      takeaway={anyCurrent ? `Bu dönemde ${n(total)} ${unit}` : `Bu dönemin ${unit} sayısı henüz gelmedi`}
      count={daily.length}
      series={[
        {
          key: channel,
          label: youtube ? 'YouTube izlenme' : 'Pinterest gösterim',
          colour: youtube ? 'var(--series-2)' : 'var(--series-4)',
          values,
        },
      ]}
      pick={pick}
      readout={readout}
      labelled={values}
      markerIndex={currentStart(daily, current)}
      ends={dayEnds(daily)}
      note={
        youtube
          ? 'Her gün sabah 07:00 okumasıyla bir önceki sabahın farkı.'
          : daily.some((day) => day.pinterestEstimated)
            ? 'Son günleri Pinterest henüz tahmini veriyor; kesinleşince güncellenir.'
            : undefined
      }
      table={{
        caption: youtube ? 'YouTube günlük izlenme' : 'Pinterest günlük gösterim',
        columns: ['Gün', youtube ? 'İzlenme' : 'Gösterim'],
        rows: daily.map((day, index) => ({
          head: formatIsoDay(day.day),
          cells: [valueOr(values[index] ?? null, n, '-') + (!youtube && day.pinterestEstimated ? ' (tahmini)' : '')],
        })),
      }}
    />
  );
}

export type { AlignedPoint };
